import { privateClient } from "@/api/privateClient";
import { SUPER_ADMIN_ENDPOINTS } from "../../endpoint";
import type { AcademicYear, AcademicYearLabel, AcademicYearUsage, InstitutionAcademicYear } from "../types";

const E = SUPER_ADMIN_ENDPOINTS.ACADEMIC_YEARS;

/** The years the signed-in user can use. */
export const listAcademicYears = async (): Promise<AcademicYear[]> => {
  const { data } = await privateClient.get(E.LIST);
  return data?.academicYears ?? data ?? [];
};

// ---- labels (super admin) ----

export const listAcademicYearLabels = async (): Promise<AcademicYearLabel[]> => {
  const { data } = await privateClient.get(E.LABELS);
  return data?.labels ?? [];
};

export const createAcademicYearLabel = async (label: string): Promise<AcademicYearLabel> => {
  const { data } = await privateClient.post(E.LABELS, { label });
  return data.label;
};

export const renameAcademicYearLabel = async (id: number, label: string): Promise<AcademicYearLabel> => {
  const { data } = await privateClient.put(E.LABEL(id), { label });
  return data.label;
};

export const deleteAcademicYearLabel = async (id: number): Promise<void> => {
  await privateClient.delete(E.LABEL(id));
};

export const getAcademicYearLabelUsage = async (id: number): Promise<AcademicYearUsage> => {
  const { data } = await privateClient.get(E.LABEL_USAGE(id));
  return data;
};

// ---- a school's own years ----

export const listAcademicYearsForInstitution = async (institutionId: number): Promise<InstitutionAcademicYear[]> => {
  const { data } = await privateClient.get(E.LIST_FOR_INSTITUTION(institutionId));
  return data.academicYears ?? [];
};

export const setInstitutionYearDates = async (
  institutionId: number,
  labelId: number,
  payload: { startDate: string; endDate: string }
): Promise<{ academicYearId: number; startDate: string; endDate: string }> => {
  const { data } = await privateClient.put(E.SET_INSTITUTION_LABEL_DATES(institutionId, labelId), payload);
  return data;
};

export const removeInstitutionYear = async (institutionId: number, labelId: number): Promise<void> => {
  await privateClient.delete(E.SET_INSTITUTION_LABEL_DATES(institutionId, labelId));
};

/** `confirm` answers the server's "this year is far from today" question, which comes back as a 409 with needsConfirm. */
export const setInstitutionCurrentYear = async (institutionId: number, academicYearId: number, confirm = false): Promise<void> => {
  await privateClient.put(E.SET_INSTITUTION_CURRENT(institutionId), { academicYearId, ...(confirm ? { confirm: true } : {}) });
};
